<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import FieldLeaf from '@/design/primitives/FieldLeaf.vue'
import { api, setToken } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'
import { useToastStore } from '@/stores/toastStore'

const router = useRouter()
const toast  = useToastStore()

const props = defineProps({
  withdrawalsEnabled: { type: Boolean, default: true },
})

// ——————————————————————
// Sign out
// ——————————————————————
const signOutLoading = ref(false)
async function handleSignOut() {
  signOutLoading.value = true
  try {
    try { await api.post(EP.authLogout) } catch {} /* server-side cookie clear; ignore failures */
    setToken(null)
    toast.show('Signed out.', { tone: 'success', duration: 2500 })
    router.push('/')
  } finally {
    signOutLoading.value = false
  }
}

// ——————————————————————
// Display name
// ——————————————————————
const nameFirst    = ref('')
const nameSurname  = ref('')
const nameLoading  = ref(false)
const nameSaving   = ref(false)
const nameError    = ref(null)
const nameOk       = ref(false)

async function loadName() {
  nameLoading.value = true
  nameError.value   = null
  try {
    const res = await api.get(EP.userName)
    nameFirst.value   = res.data.name    ?? ''
    nameSurname.value = res.data.surname ?? ''
  } catch (e) {
    nameError.value = e.message || 'Could not load name.'
  } finally {
    nameLoading.value = false
  }
}

async function saveName() {
  nameSaving.value = true
  nameOk.value     = false
  nameError.value  = null
  try {
    await api.put(EP.userName, { name: nameFirst.value, surname: nameSurname.value })
    nameOk.value = true
  } catch (e) {
    nameError.value = e.message || 'Could not save name.'
  } finally {
    nameSaving.value = false
  }
}

// ——————————————————————
// Payout info
// ——————————————————————
const payoutMethod  = ref('card')
const payoutDetails = ref({ cardNumber: '', cardHolder: '', iban: '', accountHolder: '' })
const payoutLoading = ref(false)
const payoutSaving  = ref(false)
const payoutError   = ref(null)
const payoutOk      = ref(false)

async function loadPayout() {
  payoutLoading.value = true
  payoutError.value   = null
  try {
    const res = await api.get(EP.userPayoutInfo)
    payoutMethod.value  = res.data.payoutMethod ?? 'card'
    const d = res.data.payoutDetails ?? {}
    payoutDetails.value = {
      cardNumber:    d.cardNumber    ?? '',
      cardHolder:    d.cardHolder    ?? '',
      iban:          d.iban          ?? '',
      accountHolder: d.accountHolder ?? ''
    }
  } catch (e) {
    payoutError.value = e.message || 'Could not load payout info.'
  } finally {
    payoutLoading.value = false
  }
}

async function savePayout() {
  payoutSaving.value = true
  payoutOk.value     = false
  payoutError.value  = null
  const details = payoutMethod.value === 'card'
    ? { cardNumber: payoutDetails.value.cardNumber, cardHolder: payoutDetails.value.cardHolder }
    : { iban: payoutDetails.value.iban, accountHolder: payoutDetails.value.accountHolder }
  try {
    await api.put(EP.userPayoutInfo, { payoutMethod: payoutMethod.value, payoutDetails: details })
    payoutOk.value = true
  } catch (e) {
    payoutError.value = e.message || 'Could not save payout info.'
  } finally {
    payoutSaving.value = false
  }
}

// ——————————————————————
// Danger zone — delete account
// ——————————————————————
const deleteConfirmText = ref('')
const deleteConfirmOpen = ref(false)
const deleteError       = ref(null)
const deleting          = ref(false)

async function deleteAccount() {
  if (deleteConfirmText.value !== 'DELETE') return
  deleting.value    = true
  deleteError.value = null
  try {
    await api.delete(EP.authDelete)
    setToken(null)
    router.push('/')
  } catch (e) {
    deleteError.value = e.message || 'Could not delete account.'
  } finally {
    deleting.value = false
  }
}

onMounted(() => {
  loadName()
  if (props.withdrawalsEnabled) loadPayout()
})
</script>

<template>
  <section class="dossier-section" aria-labelledby="settings-heading">
    <div class="settings-head">
      <h2 id="settings-heading" class="stamp u-display u-allcaps">Settings</h2>
      <ButtonSeed variant="ghost" size="sm" :disabled="signOutLoading" @click="handleSignOut">
        {{ signOutLoading ? 'SIGNING OUT…' : 'SIGN OUT' }}
      </ButtonSeed>
    </div>

    <!-- Display name -->
    <PanelOrganic tone="strong" :padded="true">
      <h3 class="panel-label u-display u-allcaps">Display name</h3>

      <div v-if="nameLoading" class="inline-state" aria-live="polite">
        <span class="dossier-spinner" aria-hidden="true" />
        <span class="inline-state__text u-display u-allcaps">Loading…</span>
      </div>

      <div v-else class="settings-form">
        <FieldLeaf v-model="nameFirst"   label="FIRST NAME"  placeholder="Jane" />
        <FieldLeaf v-model="nameSurname" label="LAST NAME"   placeholder="Doe"  />
        <div class="settings-form__actions">
          <ButtonSeed
            variant="primary"
            size="sm"
            :disabled="nameSaving"
            @click="saveName"
          >
            {{ nameSaving ? 'SAVING…' : 'SAVE NAME' }}
          </ButtonSeed>
          <span v-if="nameOk"    class="settings-ok  u-display u-allcaps" role="status">Saved.</span>
          <span v-if="nameError" class="settings-err u-display u-allcaps" role="alert">{{ nameError }}</span>
        </div>
      </div>
    </PanelOrganic>

    <!-- Payout info -->
    <PanelOrganic v-if="props.withdrawalsEnabled" tone="strong" :padded="true">
      <h3 class="panel-label u-display u-allcaps">Payout information</h3>

      <div v-if="payoutLoading" class="inline-state" aria-live="polite">
        <span class="dossier-spinner" aria-hidden="true" />
        <span class="inline-state__text u-display u-allcaps">Loading…</span>
      </div>

      <div v-else class="settings-form">
        <!-- Method selector -->
        <fieldset class="method-chooser">
          <legend class="method-chooser__legend u-display u-allcaps">Payout method</legend>
          <div class="method-chooser__options">
            <label class="method-chooser__option" :class="{ 'method-chooser__option--active': payoutMethod === 'card' }">
              <input type="radio" value="card" v-model="payoutMethod" class="method-chooser__radio" />
              CARD
            </label>
            <label class="method-chooser__option" :class="{ 'method-chooser__option--active': payoutMethod === 'sepa' }">
              <input type="radio" value="sepa" v-model="payoutMethod" class="method-chooser__radio" />
              SEPA / BANK
            </label>
          </div>
        </fieldset>

        <!-- Card fields -->
        <template v-if="payoutMethod === 'card'">
          <FieldLeaf v-model="payoutDetails.cardNumber" label="CARD NUMBER"   placeholder="**** **** **** ****" :mono="true" />
          <FieldLeaf v-model="payoutDetails.cardHolder" label="CARDHOLDER NAME" placeholder="Jane Doe" />
        </template>

        <!-- SEPA fields -->
        <template v-else>
          <FieldLeaf v-model="payoutDetails.iban"          label="IBAN"           placeholder="GB00 XXXX XXXX XXXX XXXX XX" :mono="true" />
          <FieldLeaf v-model="payoutDetails.accountHolder" label="ACCOUNT HOLDER" placeholder="Jane Doe" />
        </template>

        <div class="settings-form__actions">
          <ButtonSeed
            variant="primary"
            size="sm"
            :disabled="payoutSaving"
            @click="savePayout"
          >
            {{ payoutSaving ? 'SAVING…' : 'SAVE PAYOUT INFO' }}
          </ButtonSeed>
          <span v-if="payoutOk"    class="settings-ok  u-display u-allcaps" role="status">Saved.</span>
          <span v-if="payoutError" class="settings-err u-display u-allcaps" role="alert">{{ payoutError }}</span>
        </div>
      </div>
    </PanelOrganic>

    <!-- Danger zone -->
    <PanelOrganic tone="solid" :padded="true" class="danger-zone">
      <h3 class="panel-label danger-zone__label u-display u-allcaps">Danger zone</h3>
      <p class="danger-zone__body">
        Permanently delete your account and all associated data. This cannot be undone.
      </p>

      <template v-if="!deleteConfirmOpen">
        <ButtonSeed variant="ghost" size="sm" class="danger-zone__trigger" @click="deleteConfirmOpen = true">
          DELETE ACCOUNT
        </ButtonSeed>
      </template>

      <template v-else>
        <div class="delete-confirm">
          <p class="delete-confirm__prompt">
            Type <strong class="u-gold">DELETE</strong> to confirm permanent deletion.
          </p>
          <div class="delete-confirm__row">
            <FieldLeaf
              v-model="deleteConfirmText"
              label="CONFIRMATION"
              placeholder="DELETE"
              :mono="true"
            />
            <div class="delete-confirm__actions">
              <ButtonSeed
                variant="copper"
                size="md"
                :disabled="deleteConfirmText !== 'DELETE' || deleting"
                @click="deleteAccount"
              >
                {{ deleting ? 'DELETING…' : 'CONFIRM DELETE' }}
              </ButtonSeed>
              <ButtonSeed
                variant="ghost"
                size="md"
                @click="deleteConfirmOpen = false; deleteConfirmText = ''"
              >
                CANCEL
              </ButtonSeed>
            </div>
          </div>
          <span v-if="deleteError" class="settings-err u-display u-allcaps" role="alert">{{ deleteError }}</span>
        </div>
      </template>
    </PanelOrganic>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.settings-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
  flex-wrap: wrap;
  margin: 0 0 var(--sp-2);
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

.panel-label {
  font-size: var(--fs-16);
  letter-spacing: 0.12em;
  color: var(--ink);
  margin: 0 0 var(--sp-6);
}

/* Settings form */
.settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.settings-form__actions {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  flex-wrap: wrap;
  margin-top: var(--sp-2);
}

.settings-ok {
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--gold);
}

.settings-err {
  font-size: var(--fs-12);
  letter-spacing: 0.1em;
  color: var(--ember);
}

/* Loading inline */
.inline-state {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-4);
  color: var(--ink-soft);
}
.inline-state__text { font-size: var(--fs-12); }

/* Method chooser */
.method-chooser {
  border: none;
  padding: 0;
  margin: 0;
}

.method-chooser__legend {
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--ink-soft);
  margin-bottom: var(--sp-3);
  display: block;
}

.method-chooser__options {
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
}

.method-chooser__radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.method-chooser__option {
  display: inline-flex;
  align-items: center;
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--r-sm);
  border: 1px solid var(--ash-strong);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  color: var(--ink-soft);
  background: transparent;
  transition: border-color var(--t-fast) var(--ease-cinema),
              background   var(--t-fast) var(--ease-cinema),
              color        var(--t-fast) var(--ease-cinema);
}
.method-chooser__option:hover {
  background: var(--bg-deep);
  color: var(--ink);
}
.method-chooser__option--active {
  border-color: var(--gold);
  color: var(--gold);
}

/* Danger zone */
.danger-zone {
  border: 1px solid var(--ember) !important;
}

.danger-zone__label {
  color: var(--ember);
  margin: 0 0 var(--sp-3);
}

.danger-zone__body {
  font-size: var(--fs-14);
  color: var(--ink-soft);
  margin: 0 0 var(--sp-6);
  line-height: var(--lh-body);
}

.danger-zone__trigger {
  border-color: var(--ember) !important;
  color: var(--ember) !important;
}
.danger-zone__trigger:hover {
  background: rgba(230, 76, 43, 0.1) !important;
}

/* Delete confirm flow */
.delete-confirm {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.delete-confirm__prompt {
  font-size: var(--fs-14);
  color: var(--ink-soft);
  margin: 0;
}

.delete-confirm__row {
  display: flex;
  align-items: flex-end;
  gap: var(--sp-4);
  flex-wrap: wrap;
}

.delete-confirm__row > :first-child { flex: 1; min-width: 180px; }

.delete-confirm__actions {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
  padding-bottom: 0;
}

/* Spinner */
.dossier-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
